import React, { createContext, useEffect, useReducer } from "react";

import EnsureUserMutation from "api/mutations/EnsureUserMutation";
import useMutation from "hooks/useMutation";

import { getDeviceToken, setDeviceToken } from "utility/deviceToken"; // for in-memory only access token

const ACTIONS = {
	tokenLoaded: "TOKEN_LOADED",
	setLocale: "SET_LOCALE"
};

const initialState = {
	tokenLoaded: false,
	locale: "en",
	setLocale: (_locale: string) => {}
};

interface AppContextProps {
	tokenLoaded: boolean;
	locale: string;
	setLocale: (locale: string) => void;
}

export const AppContext = createContext<AppContextProps>(initialState);

const reducer = (state, action) => {
	switch (action.type) {
		case ACTIONS.tokenLoaded:
			return {
				...state,
				tokenLoaded: true
			};

		case ACTIONS.setLocale:
			return {
				...state,
				locale: action.payload
			};

		default:
			console.warn("app reducer fall thru");
			return state;
	}
};

interface AppContextProviderProps {
	children: React.ReactNode;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({ children }) => {
	const [{ tokenLoaded, ...state }, dispatch] = useReducer(reducer, initialState);

	const [ensureUser, { called, loading, data }] = useMutation(EnsureUserMutation, {
		variables: {
			ensureUserInput: {
				deviceToken: state.deviceToken
			}
		}
	});

	const deviceToken = data?.ensureUser?.deviceToken || null;

	useEffect(() => {
		const token = getDeviceToken();
		if (token) {
			dispatch({ type: ACTIONS.tokenLoaded });
		}
		ensureUser().catch(err => console.error("ensureUser() failed:", err));
	}, [ensureUser]);

	useEffect(() => {
		if (called && !loading) {
			if (deviceToken !== getDeviceToken()) {
				setDeviceToken(deviceToken);
			}

			if (deviceToken) {
				dispatch({ type: ACTIONS.tokenLoaded });
			}
		}
	}, [loading, deviceToken, called]);

	const setLocale = React.useCallback(
		(locale: string) => {
			if (locale !== state.locale) {
				dispatch({
					type: ACTIONS.setLocale,
					payload: locale
				});
			}
		},
		[dispatch, state.locale]
	);

	return (
		<AppContext.Provider
			value={{
				...state,
				tokenLoaded,
				setLocale
			}}
		>
			{children}
		</AppContext.Provider>
	);
};
